import { APITypes } from '../../primary-record-types';
import { reportAnalyticsError } from './reportError';
import { sendAnalyticsEvents } from './send';

type AnalyticsEvent = APITypes.analyticsService.AnalyticsEventInput;

/** Per-request ceilings enforced by zRecordAnalyticsEventsRequest; a batch over either is rejected. */
const MAX_EVENTS_PER_REQUEST = 50;
const MAX_REQUEST_BYTES = 6 * 1024;

/** Buffer size and cadence carried over from the Pinpoint SDK so event volume stays comparable. */
const MAX_BUFFERED_EVENTS = 1000;
const FLUSH_INTERVAL_MS = 5000;

let buffer: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | undefined;

/**
 * UTF-8 byte length of the serialized array. The server measures the same way with Buffer.byteLength,
 * which exists in neither a browser nor React Native.
 */
const byteLength = (events: AnalyticsEvent[]) => new TextEncoder().encode(JSON.stringify(events)).length;

/**
 * Split events into requests that satisfy both ceilings. An event too large to travel on its own is
 * dropped rather than sent to a certain rejection.
 */
const toBatches = (events: AnalyticsEvent[]): AnalyticsEvent[][] => {
    const batches: AnalyticsEvent[][] = [];
    let current: AnalyticsEvent[] = [];

    for (const event of events) {
        if (byteLength([event]) > MAX_REQUEST_BYTES) continue;

        const candidate = [...current, event];
        if (candidate.length > MAX_EVENTS_PER_REQUEST || byteLength(candidate) > MAX_REQUEST_BYTES) {
            batches.push(current);
            current = [event];
        } else {
            current = candidate;
        }
    }

    if (current.length) batches.push(current);
    return batches;
};

const stopTimer = () => {
    if (!flushTimer) return;
    clearInterval(flushTimer);
    flushTimer = undefined;
};

/**
 * Send everything buffered. A batch that fails is dropped: telemetry is best-effort, and re-sending
 * risks duplicating records the service already accepted.
 */
export const flushAnalyticsEvents = async () => {
    if (!buffer.length) return;

    try {
        const events = buffer;
        buffer = [];
        stopTimer();

        const batches = toBatches(events);
        await Promise.all(batches.map((batch) => sendAnalyticsEvents(batch).catch(() => undefined)));
    } catch (error) {
        reportAnalyticsError('Failed to flush analytics', error);
    }
};

export const enqueueAnalyticsEvent = (event: AnalyticsEvent) => {
    // Full buffer drops the newest event, matching the SDK this replaces.
    if (buffer.length >= MAX_BUFFERED_EVENTS) return;

    buffer.push(event);

    if (buffer.length >= MAX_EVENTS_PER_REQUEST) {
        void flushAnalyticsEvents();
        return;
    }

    if (!flushTimer) flushTimer = setInterval(() => void flushAnalyticsEvents(), FLUSH_INTERVAL_MS);
};
